import {
  type CompanyDto,
  type KsefAuthMethod,
  type KsefCertificateDto,
  ksefCertificateUploadSchema,
  type KsefConnectionDto,
  ksefConnectionUpdateSchema,
  KSEF_AUTH_METHODS,
  KSEF_MODES,
  KSEF_SUBJECT_TYPES,
  type KsefMode,
  type KsefSubjectType,
  datePl,
} from '@bqcrm/shared';
import { Alert, Badge, Button, CopyButton, FileButton, Group, List, Paper, PasswordInput, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { IconCertificate, IconCheck, IconCopy, IconPlugConnected, IconTrash, IconUpload } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { ask } from '../confirm';
import { formatDateTime } from '../format';
import { notifyError, notifyOk } from '../notify';
import { QueryState } from './QueryState';
import { FilterChips } from './ui';

export const useKsefConnection = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ['ksef-connection', companyId], queryFn: () => api<KsefConnectionDto>(`/companies/${companyId}/integrations/ksef`), enabled });

export const ksefBadge = (c: KsefConnectionDto | undefined, failed = false) =>
  failed ? (
    <Badge variant="light" color="red" size="sm">
      KSeF: nie udało się sprawdzić
    </Badge>
  ) : !c ? (
    <Badge variant="light" color="gray" size="sm">
      KSeF: …
    </Badge>
  ) : c.mode === 'wylaczona' ? (
    <Badge variant="light" color="gray" size="sm">
      KSeF: wyłączone
    </Badge>
  ) : (
    <Badge variant="light" color={c.status === 'ok' ? (c.mode === 'produkcja' ? 'green' : 'yellow') : c.status === 'blad' ? 'red' : 'gray'} size="sm">
      KSeF · {KSEF_MODES[c.mode]} · {c.auth === 'certyfikat' ? 'certyfikat · ' : ''}
      {c.status === 'ok' ? 'połączono' : c.status === 'blad' ? 'błąd' : 'nie sprawdzono'}
    </Badge>
  );

/** Plik z dysku → base64 (bez prefiksu data:). */
const fileBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Nie udało się odczytać pliku'));
    r.readAsDataURL(f);
  });

const DAY = 86_400_000;

/** Zapisany certyfikat: dla kogo, do kiedy, odcisk palca. */
function CertificateCard({ c, companyNip, mode }: { c: KsefCertificateDto; companyNip: string; mode: KsefMode }) {
  const daysLeft = Math.floor((Date.parse(c.validTo) - Date.now()) / DAY);
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap={6}>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <IconCertificate size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
            {c.subject}
          </Text>
        </Group>
        <Group gap={6}>
          {c.identifier && (
            <Badge variant="light" size="sm" color="blue">
              {c.identifierType} {c.identifier}
            </Badge>
          )}
          <Badge variant="light" size="sm" color="gray">
            {c.keyType}
          </Badge>
          <Badge variant="light" size="sm" color={daysLeft < 30 ? 'orange' : 'green'}>
            ważny do {datePl(c.validTo.slice(0, 10))}
            {daysLeft < 30 ? ` (${daysLeft} dni)` : ''}
          </Badge>
          {c.selfSigned && (
            <Badge variant="light" size="sm" color="yellow">
              samopodpisany
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
          Wystawca: {c.issuer}
        </Text>
        <Group gap={4} wrap="nowrap">
          <Text size="xs" c="dimmed" ff="monospace" style={{ wordBreak: 'break-all' }}>
            SHA-256: {c.fingerprint}
          </Text>
          <CopyButton value={c.fingerprint}>
            {({ copied, copy }) => (
              <Button size="compact-xs" variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy} style={{ flexShrink: 0 }} leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}>
                {copied ? 'Skopiowano' : 'Kopiuj'}
              </Button>
            )}
          </CopyButton>
        </Group>
        {c.setAt && (
          <Text size="xs" c="dimmed">
            Wgrano {formatDateTime(c.setAt)}
          </Text>
        )}
        {c.selfSigned && mode !== 'test' && mode !== 'wylaczona' && (
          <Alert color="orange" variant="light" p="xs">
            Certyfikat samopodpisany KSeF przyjmuje tylko na środowisku testowym.
          </Alert>
        )}
        {c.identifierType === 'NIP' && companyNip && c.identifier !== companyNip && (
          <Alert color="blue" variant="light" p="xs">
            Certyfikat jest wystawiony na NIP {c.identifier}, a firma ma NIP {companyNip}. Logowanie zadziała, jeśli ten podmiot ma w KSeF uprawnienie do faktur firmy.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

/** Wgranie certyfikatu: certyfikat KSeF (.crt + .key) albo plik PKCS#12 (.p12 / .pfx). */
function CertificateUpload({ companyId, onDone }: { companyId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [format, setFormat] = useState<'pem' | 'p12'>('pem');
  const [cert, setCert] = useState<File | null>(null);
  const [key, setKey] = useState<File | null>(null);
  const [p12, setP12] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const upload = useMutation({
    mutationFn: async () => {
      const body =
        format === 'pem'
          ? { format, certificate: await fileBase64(cert!), privateKey: await fileBase64(key!), password: password || undefined }
          : { format, file: await fileBase64(p12!), password: password || undefined };
      return api<KsefConnectionDto>(`/companies/${companyId}/integrations/ksef/certificate`, { method: 'POST', body: ksefCertificateUploadSchema.parse(body) });
    },
    onSuccess: (d) => {
      qc.setQueryData(['ksef-connection', companyId], d);
      setPassword('');
      notifyOk('Wgrano certyfikat');
      onDone();
    },
    onError: notifyError,
  });
  const ready = format === 'pem' ? !!cert && !!key : !!p12;
  const pick = (label: string, file: File | null, set: (f: File | null) => void, accept: string) => (
    <Group gap="xs" wrap="nowrap">
      <FileButton onChange={set} accept={accept}>
        {(props) => (
          <Button {...props} variant="default" size="xs" leftSection={<IconUpload size={14} />} style={{ flexShrink: 0 }}>
            {label}
          </Button>
        )}
      </FileButton>
      <Text size="xs" c={file ? undefined : 'dimmed'} truncate>
        {file ? file.name : 'nie wybrano'}
      </Text>
    </Group>
  );
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="sm">
        <FilterChips
          size="xs"
          value={format}
          onChange={setFormat}
          options={[
            { value: 'pem', label: 'Certyfikat KSeF (.crt + .key)' },
            { value: 'p12', label: 'Plik .p12 / .pfx' },
          ]}
        />
        {format === 'pem' ? (
          <>
            {pick('Certyfikat', cert, setCert, '.crt,.cer,.pem,.der')}
            {pick('Klucz prywatny', key, setKey, '.key,.pem,.der')}
          </>
        ) : (
          pick('Plik certyfikatu', p12, setP12, '.p12,.pfx')
        )}
        <PasswordInput
          label={format === 'pem' ? 'Hasło do klucza prywatnego' : 'Hasło do pliku'}
          description="Potrzebne tylko do odczytu — hasło nie jest zapisywane"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          autoComplete="off"
        />
        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={onDone}>
            Anuluj
          </Button>
          <Button leftSection={<IconCertificate size={16} />} disabled={!ready} loading={upload.isPending} onClick={() => upload.mutate()}>
            Wgraj certyfikat
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

/** Połączenie firmy z KSeF (faktury zakupu): środowisko, logowanie tokenem albo certyfikatem, pobieranie od dnia, automatyczne pobieranie. */
export function KsefSettings({ company }: { company: CompanyDto }) {
  const qc = useQueryClient();
  const q = useKsefConnection(company.id);
  const [mode, setMode] = useState<KsefMode>('wylaczona');
  const [auth, setAuth] = useState<KsefAuthMethod>('token');
  const [subjectType, setSubjectType] = useState<KsefSubjectType>('certificateSubject');
  const [token, setToken] = useState('');
  const [since, setSince] = useState('');
  const [autoSync, setAutoSync] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [check, setCheck] = useState<{ ok: boolean; message: string } | null>(null);
  // stan formularza z serwera tylko przy pierwszym wczytaniu — wgranie certyfikatu nie kasuje niezapisanych wyborów
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!q.data || loaded) return;
    setLoaded(true);
    setMode(q.data.mode);
    setAuth(q.data.auth);
    setSubjectType(q.data.subjectType);
    setSince(q.data.since ?? '');
    setAutoSync(q.data.autoSync);
  }, [q.data, loaded]);
  const url = `/companies/${company.id}/integrations/ksef`;
  const save = useMutation({
    mutationFn: () => api<KsefConnectionDto>(url, { method: 'PUT', body: ksefConnectionUpdateSchema.parse({ mode, auth, subjectType, token: (auth === 'token' && token) || undefined, since: since || null, autoSync }) }),
    onSuccess: (d) => {
      qc.setQueryData(['ksef-connection', company.id], d);
      void qc.invalidateQueries({ queryKey: ['ksef-overview'] });
      setToken('');
      setCheck(null);
      notifyOk('Zapisano połączenie z KSeF');
    },
    onError: notifyError,
  });
  const test = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>(`${url}/check`, { method: 'POST' }),
    onSuccess: (r) => {
      setCheck(r);
      void qc.invalidateQueries({ queryKey: ['ksef-connection', company.id] });
    },
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: () => api<KsefConnectionDto>(`${url}/certificate`, { method: 'DELETE' }),
    onSuccess: (d) => {
      qc.setQueryData(['ksef-connection', company.id], d);
      setCheck(null);
      notifyOk('Usunięto certyfikat');
    },
    onError: notifyError,
  });
  const d = q.data;
  const switchingEnv = !!d && d.mode !== 'wylaczona' && mode !== d.mode && mode !== 'wylaczona';
  const dirty = !!d && (mode !== d.mode || auth !== d.auth || subjectType !== d.subjectType || !!token || (since || null) !== d.since || autoSync !== d.autoSync);
  const submit = async () => {
    if (mode === 'produkcja' && d?.mode !== 'produkcja' && !(await ask('Faktury zakupu będą pobierane z produkcyjnego KSeF — to prawdziwe dokumenty księgowe firmy.', { title: 'Włączyć produkcyjne KSeF?', confirm: 'Włącz' }))) return;
    if (switchingEnv && !(await ask('Pobieranie zacznie się od nowa (od daty „od”). Dokumenty z poprzedniego środowiska zostaną w systemie z oznaczeniem środowiska.', { title: 'Zmienić środowisko KSeF?', confirm: 'Zmień' }))) return;
    save.mutate();
  };
  const removeCert = async () => {
    if (
      !(await ask(d?.auth === 'certyfikat' ? 'Pobieranie z KSeF przestanie działać, dopóki nie wgrasz nowego certyfikatu albo nie przełączysz logowania na token.' : 'Certyfikat i klucz zostaną usunięte z systemu.', {
        title: 'Usunąć certyfikat?',
        confirm: 'Usuń',
        danger: true,
      }))
    )
      return;
    remove.mutate();
  };

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {d && (
        <Stack gap="md">
          <Text size="sm">
            Faktury zakupu (firma jako nabywca) pobierane z Krajowego Systemu e-Faktur w kontekście NIP firmy <b>{d.nip || '—'}</b>.
          </Text>
          {!d.nip && (
            <Alert color="orange" variant="light">
              Firma nie ma NIP — uzupełnij go w danych firmy.
            </Alert>
          )}
          {!d.keyConfigured && (
            <Alert color="red" variant="light">
              Serwer nie ma klucza szyfrowania (INTEGRATIONS_KEY) — token i certyfikat nie mogą zostać zapisane.
            </Alert>
          )}
          <Select label="Środowisko" data={Object.entries(KSEF_MODES).map(([value, label]) => ({ value, label }))} value={mode} onChange={(v) => setMode((v as KsefMode) ?? 'wylaczona')} allowDeselect={false} />

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Logowanie do KSeF
            </Text>
            <FilterChips value={auth} onChange={setAuth} options={(Object.keys(KSEF_AUTH_METHODS) as KsefAuthMethod[]).map((value) => ({ value, label: KSEF_AUTH_METHODS[value] }))} />
          </Stack>

          {auth === 'token' ? (
            <PasswordInput
              label="Token KSeF"
              description={d.tokenHint ? `Zapisany token …${d.tokenHint}${d.tokenSetAt ? ` (${formatDateTime(d.tokenSetAt)})` : ''} — wpisz nowy, żeby go zmienić` : 'Token nie wraca do przeglądarki po zapisaniu'}
              value={token}
              onChange={(e) => setToken(e.currentTarget.value)}
              autoComplete="off"
            />
          ) : (
            <Stack gap="sm">
              {d.certificate ? <CertificateCard c={d.certificate} companyNip={d.nip} mode={mode} /> : !uploading && <Text size="sm" c="dimmed">Nie wgrano certyfikatu.</Text>}
              {uploading ? (
                <CertificateUpload companyId={company.id} onDone={() => setUploading(false)} />
              ) : (
                <Group gap="xs">
                  <Button variant="default" size="xs" leftSection={<IconUpload size={14} />} onClick={() => setUploading(true)}>
                    {d.certificate ? 'Wgraj nowy certyfikat' : 'Wgraj certyfikat'}
                  </Button>
                  {d.certificate && (
                    <Button variant="subtle" color="red" size="xs" leftSection={<IconTrash size={14} />} loading={remove.isPending} onClick={() => void removeCert()}>
                      Usuń
                    </Button>
                  )}
                </Group>
              )}
              <Select
                label="Rozpoznawanie podmiotu"
                description="Zwykle z danych certyfikatu. Odcisk palca — gdy w KSeF nadano uprawnienia na odcisk tego certyfikatu."
                data={Object.entries(KSEF_SUBJECT_TYPES).map(([value, label]) => ({ value, label }))}
                value={subjectType}
                onChange={(v) => setSubjectType((v as KsefSubjectType) ?? 'certificateSubject')}
                allowDeselect={false}
              />
            </Stack>
          )}

          <TextInput label="Pobieraj faktury od dnia" description="Pierwsze pobranie; puste — ostatnie 30 dni. Zmiana daty pobiera od niej ponownie (bez duplikatów)." type="date" value={since} onChange={(e) => setSince(e.currentTarget.value)} />
          <Switch label="Pobieraj automatycznie co godzinę" checked={autoSync} onChange={(e) => setAutoSync(e.currentTarget.checked)} />
          <Group justify="space-between">
            <Button variant="default" leftSection={<IconPlugConnected size={16} />} loading={test.isPending} disabled={d.mode === 'wylaczona' || dirty} onClick={() => test.mutate()}>
              Sprawdź połączenie
            </Button>
            <Button loading={save.isPending} disabled={auth === 'certyfikat' && mode !== 'wylaczona' && !d.certificate} onClick={() => void submit()}>
              Zapisz
            </Button>
          </Group>
          {dirty && d.mode !== 'wylaczona' && (
            <Text size="xs" c="dimmed">
              Zapisz zmiany, żeby sprawdzić połączenie.
            </Text>
          )}
          {check && (
            <Alert color={check.ok ? 'green' : 'red'} variant="light">
              {check.message}
            </Alert>
          )}
          {!check && d.status === 'blad' && d.lastError && (
            <Alert color="red" variant="light" title="Ostatni błąd">
              {d.lastError}
            </Alert>
          )}
          {d.lastRun && (
            <Text size="xs" c="dimmed">
              Ostatnie pobranie: {formatDateTime(d.lastRun.startedAt)} ({d.lastRun.trigger === 'auto' ? 'automatyczne' : 'ręczne'}) — nowe: {d.lastRun.added}
              {d.lastRun.error ? `, błąd: ${d.lastRun.error}` : ''}
              {d.syncedTo ? ` · pobrano do ${formatDateTime(d.syncedTo)}` : ''}
            </Text>
          )}
          {auth === 'token' ? (
            <Alert color="gray" variant="light" title="Skąd wziąć token">
              <List size="sm" spacing={4}>
                <List.Item>W Aplikacji Podatnika KSeF (dla wybranego środowiska) zaloguj się jako firma i wygeneruj token.</List.Item>
                <List.Item>Nadaj mu uprawnienie do przeglądania faktur („InvoiceRead”). Do pobierania zakupów inne uprawnienia nie są potrzebne.</List.Item>
                <List.Item>Token jest pokazywany tylko raz — wklej go tutaj. W systemie jest przechowywany zaszyfrowany.</List.Item>
              </List>
            </Alert>
          ) : (
            <Alert color="gray" variant="light" title="Jaki certyfikat">
              <List size="sm" spacing={4}>
                <List.Item>
                  <b>Certyfikat KSeF</b> (zalecany) — wygeneruj go w Aplikacji Podatnika KSeF (Certyfikaty → wniosek o certyfikat do uwierzytelniania) i pobierz pliki .crt i .key. KSeF sprawdza go od razu.
                </List.Item>
                <List.Item>
                  <b>Pieczęć kwalifikowana firmy</b> (NIP) albo <b>podpis kwalifikowany</b> osoby z uprawnieniami — jako plik .p12 / .pfx. KSeF sprawdza go u wystawcy, logowanie może potrwać dłużej.
                </List.Item>
                <List.Item>Certyfikat i klucz są przechowywane zaszyfrowane; klucz nie wraca do przeglądarki, hasło do pliku nie jest zapisywane.</List.Item>
                <List.Item>Certyfikat na karcie kryptograficznej (bez pliku) nie nadaje się do pobierania automatycznego — użyj certyfikatu KSeF albo tokenu.</List.Item>
              </List>
            </Alert>
          )}
        </Stack>
      )}
    </QueryState>
  );
}
